import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link2, Loader2, Terminal, Copy, Check } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/components/ui/use-toast';
import { trpc } from '@/lib/api/trpc';
import { brandingConfig } from '@/config/branding';

interface DeployFromUrlDialogProps {
  storeId: string;
}

export function DeployFromUrlDialog({ storeId }: DeployFromUrlDialogProps) {
  const { t } = useTranslation(['storefront', 'common']);
  const { toast } = useToast();
  const utils = trpc.useUtils();
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('cli');
  const [copiedCommand, setCopiedCommand] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    url: '',
    pluginId: '',
    version: '',
    name: '',
    description: '',
    landingImgUrl: '',
    isPublic: false,
    mode: 'direct-mode' as 'iframe-bridge' | 'direct-mode',
    config: '{}',
    pages: '[]',
  });

  const [manifestJson, setManifestJson] = useState('');

  const deployMutation = trpc.plugins.deployPluginFromUrl.useMutation({
    onSuccess: (result) => {
      toast({
        title: t('common:success'),
        description: result.success
          ? t('plugins.deployUrl.toastDeploySuccess')
          : t('plugins.deployUrl.toastDeployFail'),
      });
      utils.plugins.list.invalidate();
      utils.plugins.listMountPoints.invalidate();
      handleClose();
    },
    onError: (error) => {
      toast({
        title: t('common:error'),
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const handleClose = () => {
    setOpen(false);
    setActiveTab('cli');
    // Reset form
    setFormData({
      url: '',
      pluginId: '',
      version: '',
      name: '',
      description: '',
      landingImgUrl: '',
      isPublic: false,
      mode: 'direct-mode',
      config: '{}',
      pages: '[]',
    });
    setManifestJson('');
  };

  const copyCommand = (command: string, id: string) => {
    navigator.clipboard.writeText(command);
    setCopiedCommand(id);
    setTimeout(() => setCopiedCommand(null), 2000);
  };

  const handleManifestPaste = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const manifestText = e.target.value;
    setManifestJson(manifestText);

    // Try to parse and auto-fill
    if (manifestText.trim()) {
      try {
        const manifest = JSON.parse(manifestText);

        // Convert entryPoints to pages format if present
        let pagesJson = formData.pages;
        if (manifest.entryPoints && Array.isArray(manifest.entryPoints)) {
          pagesJson = JSON.stringify(
            manifest.entryPoints.map((entry: any) => ({
              path: entry.path,
              features: entry.type ? [entry.type] : [],
              isDefault: entry.isDefault,
            })),
            null,
            2,
          );
        } else if (manifest.pages) {
          pagesJson = JSON.stringify(manifest.pages, null, 2);
        }

        // Build config from manifest fields
        let configJson = formData.config;
        const configObj: any = {};

        // Include router, features, requirements, etc.
        if (manifest.router) configObj.router = manifest.router;
        if (manifest.features) configObj.features = manifest.features;
        if (manifest.requirements) configObj.requirements = manifest.requirements;
        if (manifest.category) configObj.category = manifest.category;
        if (manifest.tags) configObj.tags = manifest.tags;
        if (manifest.config) Object.assign(configObj, manifest.config);

        if (Object.keys(configObj).length > 0) {
          configJson = JSON.stringify(configObj, null, 2);
        }

        setFormData({
          url: formData.url, // Keep URL as it might not be in manifest
          pluginId: manifest.pluginId || manifest.id || formData.pluginId,
          version: manifest.version || formData.version,
          name: manifest.name || formData.name,
          description: manifest.description || formData.description,
          landingImgUrl: manifest.landingImgUrl || manifest.landingImage || formData.landingImgUrl,
          isPublic: manifest.public !== undefined ? manifest.public : formData.isPublic,
          mode: (manifest.mode as any) || formData.mode,
          config: configJson,
          pages: pagesJson,
        });

        toast({
          title: t('plugins.deployUrl.toastManifestParsedTitle'),
          description: t('plugins.deployUrl.toastManifestParsedDesc'),
        });
      } catch (error) {
        // Invalid JSON, ignore
      }
    }
  };

  const handleDeploy = async () => {
    if (!formData.url || !formData.pluginId || !formData.version || !formData.name) {
      toast({
        title: t('common:error'),
        description: t('plugins.deployUrl.toastRequiredFields'),
        variant: 'destructive',
      });
      return;
    }

    try {
      JSON.parse(formData.config);
      JSON.parse(formData.pages);
    } catch (error) {
      toast({
        title: t('common:error'),
        description: t('plugins.deployUrl.toastInvalidJson'),
        variant: 'destructive',
      });
      return;
    }

    await deployMutation.mutateAsync({
      url: formData.url,
      pluginId: formData.pluginId,
      version: formData.version,
      name: formData.name,
      description: formData.description || undefined,
      storeId,
      mode: formData.mode,
      config: JSON.parse(formData.config),
      pages: JSON.parse(formData.pages),
      landingImgUrl: formData.landingImgUrl || undefined,
      public: formData.isPublic,
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Terminal className="mr-2 h-4 w-4" />
          {t('plugins.deployUrl.triggerBtn')}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('plugins.deployUrl.dialogTitle')}</DialogTitle>
          <DialogDescription>
            {t('plugins.deployUrl.dialogDescription')}
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="cli" className="gap-2">
              <Terminal className="h-4 w-4" />
              {t('plugins.deployUrl.cliTab')}
            </TabsTrigger>
            <TabsTrigger value="url" className="gap-2">
              <Link2 className="h-4 w-4" />
              {t('plugins.deployUrl.urlTab')}
            </TabsTrigger>
          </TabsList>

          {/* Deploy from CLI Tab */}
          <TabsContent value="cli" className="mt-4">
            <div className="space-y-6 py-4">
              {/* Introduction */}
              <div className="bg-muted/30 rounded-lg border p-4">
                <h4 className="mb-2 font-semibold">{t('plugins.deployUrl.cliIntroTitle')}</h4>
                <p className="text-muted-foreground text-sm">
                  {t('plugins.deployUrl.cliIntroPrefix')}{' '}
                  <code className="bg-muted rounded px-1">plugin.manifest.json</code>{' '}
                  {t('plugins.deployUrl.cliIntroSuffix')}
                </p>
              </div>

              {/* Installation */}
              <div className="space-y-3">
                <h4 className="font-semibold">{t('plugins.deployUrl.installCliStep')}</h4>
                <div className="relative">
                  <pre className="bg-muted rounded-lg border p-4 font-mono text-sm">
                    npm install -g @tagadapay/plugin-cli@^3.0.0
                  </pre>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute right-2 top-2 h-8 w-8"
                    onClick={() => copyCommand('npm install -g @tagadapay/plugin-cli@^3.0.0', 'install')}
                  >
                    {copiedCommand === 'install' ? (
                      <Check className="h-4 w-4 text-green-500" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>

              {/* Deploy Command */}
              <div className="space-y-3">
                <h4 className="font-semibold">{t('plugins.deployUrl.deployStep')}</h4>
                <p className="text-muted-foreground text-sm">{t('plugins.deployUrl.deployStepIntro')}</p>
                <div className="relative">
                  <pre className="bg-muted rounded-lg border p-4 font-mono text-sm">tgdcli deploy</pre>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute right-2 top-2 h-8 w-8"
                    onClick={() => copyCommand('tgdcli deploy', 'deploy')}
                  >
                    {copiedCommand === 'deploy' ? (
                      <Check className="h-4 w-4 text-green-500" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
                <div className="bg-muted/50 rounded-lg border p-3">
                  <p className="text-muted-foreground mb-2 text-sm font-medium">{t('plugins.deployUrl.cliWillLabel')}</p>
                  <ul className="text-muted-foreground ml-4 list-disc space-y-1 text-sm">
                    <li>
                      {t('plugins.deployUrl.cliStepRead')} <code className="bg-muted rounded px-1">plugin.manifest.json</code>
                    </li>
                    <li>{t('plugins.deployUrl.cliStepSelectStore')}</li>
                    <li>{t('plugins.deployUrl.cliStepBuild')}</li>
                    <li>{t('plugins.deployUrl.cliStepDeploy')}</li>
                    <li>
                      {t('plugins.deployUrl.cliStepSyncPrefix')}{' '}
                      <code className="bg-muted rounded px-1">config/</code> {t('plugins.deployUrl.cliStepSyncSuffix')}
                    </li>
                  </ul>
                </div>
              </div>

              {/* Example Output */}
              <div className="space-y-3">
                <h4 className="font-semibold">{t('plugins.deployUrl.exampleOutputTitle')}</h4>
                <div className="bg-muted rounded-lg border p-4">
                  <pre className="font-mono text-xs leading-relaxed">
                    {`📄 Reading plugin manifest from: plugin.manifest.json
🔍 Loading stores...
✅ Found 7 store(s)

✔ Select a store: ${storeId}
🚀 Deploying plugin: Native Checkout V2
📊 Version: 2.1.0
📦 Build directory: /dist

📦 Creating deployment archive...
✓ Archive created: 2.72 MB

☁️  Uploading to blob storage...
✓ Uploaded successfully

🚀 Deploying via API...
✅ Deployment successful!`}
                  </pre>
                </div>
              </div>

              {/* Advanced Features */}
              <div className="space-y-3">
                <h4 className="font-semibold">{t('plugins.deployUrl.advancedFeaturesTitle')}</h4>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="rounded-lg border p-3">
                    <h5 className="mb-1 text-sm font-semibold">{t('plugins.deployUrl.configPresetsTitle')}</h5>
                    <p className="text-muted-foreground text-xs">
                      {t('plugins.deployUrl.configPresetsPrefix')} <code className="bg-muted rounded px-1">*.config.json</code> {t('plugins.deployUrl.configPresetsMid')}{' '}
                      <code className="bg-muted rounded px-1">config/</code> {t('plugins.deployUrl.configPresetsSuffix')}
                    </p>
                  </div>
                  <div className="rounded-lg border p-3">
                    <h5 className="mb-1 text-sm font-semibold">{t('plugins.deployUrl.schemaFilesTitle')}</h5>
                    <p className="text-muted-foreground text-xs">
                      {t('plugins.deployUrl.schemaFilesPrefix')} <code className="bg-muted rounded px-1">schema.json</code> {t('plugins.deployUrl.schemaFilesAnd')}{' '}
                      <code className="bg-muted rounded px-1">ui-schema.json</code> {t('plugins.deployUrl.schemaFilesSuffix')}{' '}
                      <code className="bg-muted rounded px-1">config/</code>
                    </p>
                  </div>
                  <div className="rounded-lg border p-3">
                    <h5 className="mb-1 text-sm font-semibold">{t('plugins.deployUrl.localDevTitle')}</h5>
                    <p className="text-muted-foreground text-xs">
                      {t('plugins.deployUrl.localDevPrefix')} <code className="bg-muted rounded px-1">tgdcli deploy --local</code> {t('plugins.deployUrl.localDevSuffix')}
                    </p>
                  </div>
                  <div className="rounded-lg border p-3">
                    <h5 className="mb-1 text-sm font-semibold">{t('plugins.deployUrl.autoSyncTitle')}</h5>
                    <p className="text-muted-foreground text-xs">
                      {t('plugins.deployUrl.autoSyncDesc')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Documentation Link */}
              {brandingConfig.docsUrl && (
                <div className="rounded-lg border bg-blue-50 p-4 dark:bg-blue-950/20">
                  <p className="text-sm">
                    📚 {t('plugins.deployUrl.docsIntroPrefix')}{' '}
                    <a
                      href={`${brandingConfig.docsUrl}/developer-tools/cli/introduction`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-semibold text-blue-600 hover:underline dark:text-blue-400"
                    >
                      {t('plugins.deployUrl.docsLinkText')}
                    </a>
                  </p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Deploy from URL Tab */}
          <TabsContent value="url" className="mt-4">
            <div className="space-y-4 py-4">
              {/* Info Banner */}
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-900/30 dark:bg-blue-950/20">
                <p className="text-sm text-blue-900 dark:text-blue-100">
                  <strong>📦 {t('plugins.deployUrl.zipInfoLabel')}</strong> {t('plugins.deployUrl.zipInfoDesc')}
                </p>
              </div>

              {/* Manifest Paste */}
              <div className="space-y-2">
                <Label htmlFor="manifest">{t('plugins.deployUrl.manifestLabel')}</Label>
                <Textarea
                  id="manifest"
                  placeholder={t('plugins.deployUrl.manifestPlaceholder')}
                  value={manifestJson}
                  onChange={handleManifestPaste}
                  rows={4}
                  className="font-mono text-xs"
                />
                <p className="text-muted-foreground text-xs">
                  {t('plugins.deployUrl.manifestHint')}
                </p>
              </div>

              {/* URL */}
              <div className="space-y-2">
                <Label htmlFor="url">
                  {t('plugins.deployUrl.urlLabel')} <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="url"
                  placeholder={t('plugins.deployUrl.urlPlaceholder')}
                  value={formData.url}
                  onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                />
                <p className="text-muted-foreground text-xs">
                  {t('plugins.deployUrl.urlHint')}
                </p>
              </div>

              {/* Plugin ID */}
              <div className="space-y-2">
                <Label htmlFor="pluginId">
                  {t('plugins.deployUrl.pluginIdLabel')} <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="pluginId"
                  placeholder={t('plugins.deployUrl.pluginIdPlaceholder')}
                  value={formData.pluginId}
                  onChange={(e) => setFormData({ ...formData, pluginId: e.target.value })}
                />
                <p className="text-muted-foreground text-xs">{t('plugins.deployUrl.pluginIdHint')}</p>
              </div>

              {/* Name and Version */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">
                    {t('plugins.deployUrl.nameLabel')} <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="name"
                    placeholder={t('plugins.deployUrl.namePlaceholder')}
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="version">
                    {t('plugins.deployUrl.versionLabel')} <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="version"
                    placeholder={t('plugins.deployUrl.versionPlaceholder')}
                    value={formData.version}
                    onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                  />
                </div>
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="description">{t('plugins.deployUrl.descriptionLabel')}</Label>
                <Textarea
                  id="description"
                  placeholder={t('plugins.deployUrl.descriptionPlaceholder')}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={2}
                />
              </div>

              {/* Landing Image URL */}
              <div className="space-y-2">
                <Label htmlFor="landingImgUrl">{t('plugins.deployUrl.landingImgLabel')}</Label>
                <Input
                  id="landingImgUrl"
                  placeholder={t('plugins.deployUrl.landingImgPlaceholder')}
                  value={formData.landingImgUrl}
                  onChange={(e) => setFormData({ ...formData, landingImgUrl: e.target.value })}
                />
              </div>

              {/* Config (JSON) */}
              <div className="space-y-2">
                <Label htmlFor="config">{t('plugins.deployUrl.configLabel')}</Label>
                <Textarea
                  id="config"
                  placeholder="{}"
                  value={formData.config}
                  onChange={(e) => setFormData({ ...formData, config: e.target.value })}
                  rows={3}
                  className="font-mono text-xs"
                />
              </div>

              {/* Pages (JSON) */}
              <div className="space-y-2">
                <Label htmlFor="pages">{t('plugins.deployUrl.pagesLabel')}</Label>
                <Textarea
                  id="pages"
                  placeholder="[]"
                  value={formData.pages}
                  onChange={(e) => setFormData({ ...formData, pages: e.target.value })}
                  rows={3}
                  className="font-mono text-xs"
                />
              </div>

              {/* Public/Private Toggle */}
              <div className="flex items-center justify-between space-x-2 rounded-md border p-3">
                <div className="space-y-0.5">
                  <Label htmlFor="public" className="cursor-pointer">
                    {t('plugins.deployUrl.publicListingLabel')}
                  </Label>
                  <p className="text-muted-foreground text-xs">
                    {t('plugins.deployUrl.publicListingHint')}
                  </p>
                </div>
                <Switch
                  id="public"
                  checked={formData.isPublic}
                  onCheckedChange={(checked) => setFormData({ ...formData, isPublic: checked })}
                />
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={handleClose}>
                {t('common:cancel')}
              </Button>
              <Button onClick={handleDeploy} disabled={deployMutation.isPending}>
                {deployMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {deployMutation.isPending ? t('plugins.deployUrl.deployingBtn') : t('plugins.deployUrl.deployBtn')}
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
